import React, { FC, useState } from "react";

import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { yupResolver } from "@hookform/resolvers/yup";
import { Button, Checkbox } from "@mui/material";

import { ItsMeButton, OrSeparator } from "@/components/atoms";
import { PasswordInput } from "@/components/molecules";
import { TermsOfUseDialog } from "@/components/organisms";

import { PasswordFormType, passwordValidationSchema } from "../../../shared";
import { StyledTermsOfUse } from "./styled";

type InviteDefinePasswordProps = {
    onItsmeActivateAccount: () => void;
    onDefinePassword: (password: string) => void;
    isFetching: boolean;
};

const InviteDefinePassword: FC<InviteDefinePasswordProps> = ({
    onItsmeActivateAccount,
    onDefinePassword,
    isFetching,
}) => {
    const { t } = useTranslation();
    const {
        handleSubmit,
        control,
        setValue,
        formState: { isValid, isSubmitting },
    } = useForm<PasswordFormType>({
        defaultValues: { password: "" },
        resolver: yupResolver(passwordValidationSchema),
        mode: "all",
    });

    const [termsOpen, setTermsOpen] = useState(false);
    const [termsConfirmed, setTermsConfirmed] = useState(false);

    const toggleTermsConfirmed = () => setTermsConfirmed(!termsConfirmed);

    const onSubmit = (formValues: PasswordFormType) => {
        onDefinePassword(formValues.password);
    };

    const onTermsClickHandler = (e: React.MouseEvent<HTMLSpanElement, MouseEvent>) => {
        e.preventDefault();
        setTermsOpen(true);
    };

    return (
        <>
            {termsOpen && <TermsOfUseDialog onClose={() => setTermsOpen(false)} />}

            <div className="title">{t("Invite.DefinePassword.Title")}</div>

            <StyledTermsOfUse
                name="terms"
                control={
                    <Checkbox
                        color="primary"
                        data-testid="checkbox-accept_terms"
                        onClick={toggleTermsConfirmed}
                        inputProps={{ "aria-label": t("Registration.DefinePassword.Terms") }}
                    />
                }
                label={
                    <p className="form-label-checkbox">
                        {t("Registration.DefinePassword.AcceptThe")}

                        <span className="hyperlink" role="button" onClick={onTermsClickHandler}>
                            {" "}
                            {t("Registration.DefinePassword.Terms")}
                        </span>
                    </p>
                }
                labelPlacement="end"
            />

            <div>
                <ItsMeButton
                    disabled={!termsConfirmed}
                    onClick={onItsmeActivateAccount}
                    className="d-flex full-width"
                    label={t("Invite.DefinePassword.UseItsme")}
                />

                <OrSeparator color="secondary" />

                <form onSubmit={handleSubmit(onSubmit)}>
                    <PasswordInput
                        disabled={!termsConfirmed}
                        isNew
                        name="password"
                        label={t("Registration.DefinePassword.YourPassword")}
                        placeholder={t("ResetPassword.NewPassword.Placeholder")}
                        control={control}
                        onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                            setValue("password", event.target.value, { shouldValidate: true });
                        }}
                    />

                    <div className="form-bottom-container end">
                        <Button
                            data-testid="submit-button"
                            disabled={!termsConfirmed || isFetching || isSubmitting || !isValid}
                            type="submit"
                            fullWidth
                        >
                            {t("Registration.DefinePassword.CreateAccount")}
                        </Button>
                    </div>
                </form>
            </div>
        </>
    );
};

export default InviteDefinePassword;
